import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/** Compact preview row — label and value stay left-aligned (no justify-between). */
export function ProfilePreviewRow({ label, value }) {
  if (!value || value === '---') return null;
  return (
    <div className="flex items-baseline gap-2 min-w-0">
      <span className="text-sm text-gray-600 shrink-0">{label}</span>
      <span className="text-sm font-medium text-gray-900 truncate">{value}</span>
    </div>
  );
}

/** Build preview entries — drops empty / placeholder values. */
export function buildPreviewItems(entries) {
  return (entries || []).filter((item) => item?.value && item.value !== '---');
}

/** Single-column stacked preview — avoids label/value overlap in narrow cards. */
export function ProfilePreviewGrid({ items, emptyLabel = 'No details yet.' }) {
  const visible = (items || []).filter((item) => item?.value && item.value !== '---');
  if (!visible.length) {
    return <p className="text-sm text-gray-500">{emptyLabel}</p>;
  }
  return (
    <div className="grid grid-cols-1 gap-y-2.5">
      {visible.map((item) => (
        <div key={item.label} className="min-w-0">
          <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-gray-400 leading-none">
            {item.label}
          </p>
          <p
            className={`mt-1 text-sm font-bold text-gray-900 break-words leading-snug ${
              /phone/i.test(item.label) ? 'tabular-nums' : ''
            }`}
            title={item.value}>
            {item.value}
          </p>
        </div>
      ))}
    </div>
  );
}

function isPhoneLabel(label) {
  return /phone/i.test(label || '');
}

export function ProfileRow({ label, value, highlight = false, showWhenEmpty = false, title }) {
  const empty =
    value == null ||
    value === '' ||
    value === '---' ||
    (typeof value === 'string' && !value.trim());
  if (empty && !showWhenEmpty) return null;
  const display = empty ? 'Not set' : value;
  const phoneField = isPhoneLabel(label);
  const titleText =
    title ?? (typeof display === 'string' || typeof display === 'number' ? String(display) : undefined);
  return (
    <div className="min-w-0 py-1.5 border-b border-gray-100 last:border-0 overflow-hidden">
      <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-gray-400 leading-none">
        {label}
      </p>
      <div
        className={`mt-1 text-sm font-bold break-all leading-snug ${
          phoneField ? 'tabular-nums tracking-normal break-words' : ''
        } ${empty ? 'text-gray-400 font-medium' : highlight ? 'text-teal-700' : 'text-gray-900'}`}
        title={empty ? undefined : titleText}>
        {display}
      </div>
    </div>
  );
}

export function TimelinePreview({ items, ghostItem = null }) {
  const rowClass = 'grid grid-cols-[10px_minmax(88px,auto)_minmax(0,1fr)] gap-x-2.5 items-start';
  if (!items?.length && !ghostItem) {
    return <p className="text-gray-600 text-sm">Nothing recorded yet.</p>;
  }
  return (
    <div className="space-y-2.5">
      {items.map((item, index) => (
        <div key={`${item.title}-${index}`} className={rowClass}>
          <span className="w-2.5 h-2.5 rounded-full bg-primary mt-1.5 shrink-0" />
          <p className="text-gray-700 text-sm font-semibold tabular-nums shrink-0 pt-0.5">{item.date}</p>
          <div className="min-w-0">
            <p className="text-gray-900 text-sm font-semibold leading-snug">{item.title}</p>
            {item.subtitle ? (
              <p className="text-gray-600 text-xs leading-5 mt-0.5 line-clamp-2 whitespace-pre-line">
                {item.subtitle}
              </p>
            ) : null}
          </div>
        </div>
      ))}
      {ghostItem ? (
        <div className="rounded-lg border border-dashed border-teal-200/90 bg-teal-50/40 px-2.5 py-2">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="w-2.5 h-2.5 rounded-full border-2 border-dashed border-primary shrink-0 bg-white" />
            <span className="font-semibold italic text-gray-600">{ghostItem.date}</span>
            {ghostItem.subtitle ? (
              <span className="text-gray-400 truncate">· {ghostItem.subtitle}</span>
            ) : null}
          </div>
          <p
            className="mt-1.5 pl-[18px] text-sm font-medium italic text-gray-600 leading-snug line-clamp-2"
            title={ghostItem.title}>
            {ghostItem.title}
          </p>
        </div>
      ) : null}
    </div>
  );
}

export default function ExpandableProfileSection({
  title,
  subtitle,
  icon: Icon,
  defaultExpanded = false,
  preview,
  children,
  footerAction,
  className = '',
  contentClassName = ''
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div
      className={`bg-white rounded-xl border border-gray-200 overflow-hidden transition-shadow hover:shadow-sm flex flex-col h-full ${className}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex flex-col w-full text-left">
        <div className="flex items-start justify-between gap-2 p-3 pb-2">
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            {Icon ? (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
                <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
              </div>
            ) : null}
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-bold text-gray-900 leading-tight">{title}</h3>
              {subtitle ? (
                <p className="text-xs text-gray-500 mt-0.5 truncate tabular-nums" title={subtitle}>
                  {subtitle}
                </p>
              ) : null}
            </div>
          </div>
          {expanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400 shrink-0 mt-0.5" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400 shrink-0 mt-0.5" />
          )}
        </div>

        {!expanded && preview ? (
          <div className="px-3 pb-3 pt-0">{preview}</div>
        ) : null}
      </button>

      {expanded ? (
        <div className={`border-t border-gray-100 px-3 pb-3 pt-2 space-y-0 ${contentClassName}`}>{children}</div>
      ) : null}

      {footerAction ? (
        <div className="border-t border-gray-50 px-4 py-2.5 bg-slate-50/60">{footerAction}</div>
      ) : null}
    </div>
  );
}
